import { useState } from "react";
import { MagnifyingGlass, CaretDown, FunnelSimple, X, Check } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

/* --------------------------------- Search ---------------------------------------- */
export function SearchInput({ placeholder = "Search lessons…", ...props }) {
  return (
    <div className="relative">
      <MagnifyingGlass size={22} weight="bold" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input
        type="search"
        placeholder={placeholder}
        className="min-h-[56px] w-full rounded-full border-2 border-input bg-muted/50 pl-12 pr-4 text-base font-medium text-foreground placeholder:text-muted-foreground/70 transition-all duration-200 focus:border-primary focus:bg-card focus:outline-none focus:ring-4 focus:ring-primary/20"
        {...props}
      />
    </div>
  );
}

/* ---------------------------------- Select --------------------------------------- */
export function Select({ label, options = [], error, defaultValue, ...props }) {
  return (
    <div className="w-full">
      {label && <label className="mb-2 block text-sm font-bold text-foreground">{label}</label>}
      <div className="relative">
        <select
          defaultValue={defaultValue}
          aria-invalid={!!error}
          className={cn(
            "min-h-[56px] w-full appearance-none rounded-2xl border-2 bg-muted/50 px-4 pr-12 text-base font-semibold text-foreground transition-all duration-200 focus:bg-card focus:outline-none focus:ring-4 focus:ring-primary/20",
            error ? "border-destructive focus:border-destructive focus:ring-destructive/20" : "border-input focus:border-primary",
          )}
          {...props}
        >
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        <CaretDown size={20} weight="bold" className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
      </div>
      {error && <p className="mt-2 text-sm font-semibold text-destructive-ink">{error}</p>}
    </div>
  );
}

/* ----------------------------------- Chip ---------------------------------------- */
export function Chip({ children, selected, onClick, onRemove, icon: Icon, className }) {
  const Comp = onClick ? "button" : "span";
  return (
    <Comp
      onClick={onClick}
      aria-pressed={onClick ? !!selected : undefined}
      className={cn(
        "inline-flex min-h-[40px] items-center gap-1.5 rounded-full border-2 px-4 text-sm font-bold transition-all duration-200",
        selected
          ? "border-primary bg-primary/12 text-primary-strong"
          : "border-border bg-card text-foreground hover:border-primary/40",
        onClick && "active:scale-95",
        className,
      )}
    >
      {Icon && <Icon size={15} weight="bold" />}
      {children}
      {onRemove && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="ml-0.5 rounded-full p-0.5 hover:bg-foreground/10" aria-label="Remove">
          <X size={13} weight="bold" />
        </button>
      )}
    </Comp>
  );
}

/* --------------------------------- Filter Bar ------------------------------------ */
export function FilterBar({ filters = ["All", "Maths", "English", "Reading", "Science"] }) {
  const [active, setActive] = useState("All");
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex h-10 items-center gap-1.5 rounded-full bg-muted px-3 text-sm font-bold text-muted-foreground">
        <FunnelSimple size={16} weight="bold" /> Filter
      </span>
      {filters.map((f) => (
        <Chip key={f} selected={active === f} onClick={() => setActive(f)} icon={active === f ? Check : undefined}>
          {f}
        </Chip>
      ))}
    </div>
  );
}
